import React, { useCallback, useMemo, useState } from 'react';
import { View } from 'react-native';
import { useMutation, useQuery } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';

import { PEOPLE_UNAVAILABILITY_KIND } from '../../../common/constants';
import {
	DESTROY_MY_UNAVAILABILITY,
	GET_MY_UNAVAILABILITIES,
} from '../../../common/graphql/people/roster';
import { GET_EMPLOYEE_LEAVE_BALANCES } from '../../../common/graphql/people/employee';
import {
	formatLeaveBalanceUnits,
	getVisibleLeaveBalances,
	LEAVE_BALANCE_LABELS,
} from '../../../common/utils/people/leaveBalance';
import {
	canEmployeeEditOwnUnavailability,
	canEmployeeRemoveOwnUnavailability,
	formatRosterDateRange,
	formatShiftRange,
	getTodayDeviceRosterDate,
	isUpcomingOrPendingUnavailability,
} from '../../../common/utils/people/roster';
import { useUserContext } from '../../../common/utils/context';
import Text from '../../components/text/Text';
import {
	ActionDock,
	Card,
	EmptyState,
	PrimaryButton,
	Screen,
	SecondaryButton,
	SectionTitle,
	Sheet,
	StatusBadge,
} from '../../design/components';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import RosterRow from './components/RosterRow';
import { getUnavailabilityBadgeTone, ROSTER_DATE_FORMAT } from './rosterHelpers';
import useMyRosterProfile from './useMyRosterProfile';
import useRefetchMyRosterEntries from './useRefetchMyRosterEntries';

const isLeaveEntry = entry => Number(entry?.kind) === PEOPLE_UNAVAILABILITY_KIND.LEAVE;

const getEntryDetails = entry => [
	`${isLeaveEntry(entry) ? (entry.leaveTypeLabel || 'Leave') : 'Unavailable'}${entry.allDay === false
		? ` · ${formatShiftRange(entry.startMinutes, entry.endMinutes)}`
		: ''}`,
	entry.notes,
	entry.automaticallyApproved ? 'Automatically approved' : null,
	entry.decisionNotes ? `Decision: ${entry.decisionNotes}` : null,
];

// Leave balances from Xero, upcoming and past leave/unavailability requests, and the buttons
// to request leave or add unavailability (by employment type, as in People My account).
const MyLeaveView = () => {
	const styles = useThemedStyles(createStyles);
	const navigation = useNavigation();
	const { isAVC } = useUserContext();
	const today = useMemo(() => getTodayDeviceRosterDate(), []);
	const { canRequestLeave, canRequestUnavailability } = useMyRosterProfile();
	const refetchEntries = useRefetchMyRosterEntries();
	// The entry stays set while the sheet slides away, so its content does not blank out.
	const [selectedEntry, setSelectedEntry] = useState(null);
	const [isEntrySheetOpen, setIsEntrySheetOpen] = useState(false);
	const [removingId, setRemovingId] = useState(null);
	const [removeError, setRemoveError] = useState('');

	const { data, loading, error, refetch } = useQuery(GET_MY_UNAVAILABILITIES, {
		skip: !isAVC,
		fetchPolicy: 'cache-and-network',
	});
	// Live Xero call: only for employees who can request leave. A missing Xero link or a Xero
	// failure returns [].
	const { data: leaveBalanceData } = useQuery(GET_EMPLOYEE_LEAVE_BALANCES, {
		skip: !isAVC || !canRequestLeave,
		fetchPolicy: 'cache-and-network',
	});
	const [destroyMyUnavailability] = useMutation(DESTROY_MY_UNAVAILABILITY);

	const leaveBalances = useMemo(() => (
		getVisibleLeaveBalances(leaveBalanceData?.getEmployeeLeaveBalances)
	), [leaveBalanceData]);
	const entries = useMemo(() => data?.getMyUnavailabilities || [], [data]);
	const upcomingEntries = useMemo(() => (
		entries.filter(entry => isUpcomingOrPendingUnavailability(entry, today))
	), [entries, today]);
	// Newest first; the server sorts by start date ascending.
	const pastEntries = useMemo(() => (
		entries.filter(entry => !isUpcomingOrPendingUnavailability(entry, today)).reverse()
	), [entries, today]);

	const canEditEntry = useCallback(entry => (
		(isLeaveEntry(entry) ? canRequestLeave : canRequestUnavailability) &&
		canEmployeeEditOwnUnavailability(entry)
	), [canRequestLeave, canRequestUnavailability]);

	const openEntry = useCallback((entry) => {
		setRemoveError('');
		setSelectedEntry(entry);
		setIsEntrySheetOpen(true);
	}, []);
	const closeEntry = useCallback(() => setIsEntrySheetOpen(false), []);

	const openRequestForm = useCallback((params) => {
		setIsEntrySheetOpen(false);
		navigation.navigate('MyLeaveRequestView', params);
	}, [navigation]);

	const removeEntry = useCallback(async (entry) => {
		setRemovingId(entry.id);
		setRemoveError('');
		try {
			await destroyMyUnavailability({ variables: { id: entry.id } });
			setIsEntrySheetOpen(false);
			refetchEntries().catch(refetchError => console.error('Roster refresh after removal failed', refetchError));
		}
		catch (mutationError) {
			console.error('Unavailability removal failed', mutationError);
			setRemoveError(mutationError?.message || 'Unable to remove the entry. Please try again.');
		}
		finally {
			setRemovingId(null);
		}
	}, [destroyMyUnavailability, refetchEntries]);

	const renderEntry = entry => (
		<RosterRow
			key={entry.id}
			title={formatRosterDateRange(entry.startDate, entry.endDate, ROSTER_DATE_FORMAT)}
			details={getEntryDetails(entry)}
			right={(
				<StatusBadge
					label={entry.statusLabel}
					tone={getUnavailabilityBadgeTone(entry.status)}
					size="small"
				/>
			)}
			onPress={canEditEntry(entry) || canEmployeeRemoveOwnUnavailability(entry) ? () => openEntry(entry) : undefined}
			accessibilityLabel={[
				formatRosterDateRange(entry.startDate, entry.endDate, ROSTER_DATE_FORMAT),
				...getEntryDetails(entry),
				entry.statusLabel,
			].filter(Boolean).join(', ')}
			accessibilityHint="Opens edit and remove options"
		/>
	);

	const primaryAction = canRequestLeave
		? { label: 'Request leave', onPress: () => openRequestForm({ kind: PEOPLE_UNAVAILABILITY_KIND.LEAVE }) }
		: null;
	const unavailabilityAction = canRequestUnavailability
		? { label: 'Add unavailability', onPress: () => openRequestForm({ kind: PEOPLE_UNAVAILABILITY_KIND.UNAVAILABLE }) }
		: null;
	const dockAction = primaryAction || unavailabilityAction;
	const selectedEntryCanEdit = Boolean(selectedEntry) && canEditEntry(selectedEntry);
	const selectedEntryCanRemove = Boolean(selectedEntry) && canEmployeeRemoveOwnUnavailability(selectedEntry);

	return (
		<Screen
			backLabel="My roster"
			heading="Leave"
			subheading={upcomingEntries.length
				? 'You are available by default.'
				: 'You are available by default. No upcoming leave or unavailability.'}
			isLoading={loading && !data}
			error={error && !data ? 'Unable to load your leave. Please try again.' : null}
			onRetry={() => refetch()}
			dock={dockAction ? (
				<ActionDock action={dockAction}>
					{primaryAction && unavailabilityAction ? (
						<SecondaryButton label={unavailabilityAction.label} onPress={unavailabilityAction.onPress} />
					) : null}
				</ActionDock>
			) : null}
		>
			{leaveBalances.length ? (
				<View style={styles.balanceSection}>
					<View style={styles.balances}>
						{leaveBalances.map(balance => (
							<Card key={balance.leaveCategoryCode || balance.leaveName} style={styles.balanceCard}>
								<Text style={styles.balanceLabel}>{LEAVE_BALANCE_LABELS[balance.leaveCategoryCode]}</Text>
								<Text style={styles.balanceValue}>{formatLeaveBalanceUnits(balance)}</Text>
							</Card>
						))}
					</View>
					<Text style={styles.footnote}>
						From Xero payroll. May not include leave approved since the last pay run.
					</Text>
				</View>
			) : null}

			{!entries.length ? (
				<Card>
					<EmptyState
						icon="sun"
						title="No requests"
						message="No leave or unavailability entered."
					/>
				</Card>
			) : null}
			{upcomingEntries.length ? (
				<View style={styles.section}>
					<SectionTitle title="Upcoming" />
					{upcomingEntries.map(renderEntry)}
				</View>
			) : null}
			{pastEntries.length ? (
				<View style={styles.section}>
					<SectionTitle title="Past requests" />
					{pastEntries.map(renderEntry)}
				</View>
			) : null}

			<Sheet
				isVisible={isEntrySheetOpen}
				onClose={closeEntry}
				title={isLeaveEntry(selectedEntry) ? 'Leave request' : 'Unavailability'}
				footer={selectedEntry ? (
					<View style={styles.sheetActions}>
						{selectedEntryCanEdit ? (
							<SecondaryButton
								label="Edit"
								onPress={() => openRequestForm({ entryId: selectedEntry.id })}
								disabled={Boolean(removingId)}
							/>
						) : null}
						{selectedEntryCanRemove ? (
							<PrimaryButton
								label="Remove"
								tone="danger"
								onPress={() => removeEntry(selectedEntry)}
								loading={removingId === selectedEntry.id}
							/>
						) : null}
					</View>
				) : null}
			>
				{selectedEntry ? (
					<View style={styles.sheetBody}>
						<Text style={styles.sheetTitle}>
							{formatRosterDateRange(selectedEntry.startDate, selectedEntry.endDate, ROSTER_DATE_FORMAT)}
						</Text>
						{getEntryDetails(selectedEntry).filter(Boolean).map((detail, index) => (
							<Text key={`${index}-${detail}`} style={styles.sheetText}>{detail}</Text>
						))}
						<StatusBadge
							label={selectedEntry.statusLabel}
							tone={getUnavailabilityBadgeTone(selectedEntry.status)}
							size="small"
						/>
						{removeError ? <Text style={styles.errorText}>{removeError}</Text> : null}
					</View>
				) : null}
			</Sheet>
		</Screen>
	);
};

const createStyles = ({ ui: { colors, space, type } }) => ({
	balanceSection: {
		gap: space.sm,
	},
	balances: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: space.sm,
	},
	balanceCard: {
		flexGrow: 1,
		flexBasis: '40%',
		padding: 14,
		gap: space.xxs,
	},
	balanceLabel: {
		...type.caption,
		color: colors.textSecondary,
	},
	balanceValue: {
		...type.cardTitle,
		color: colors.text,
	},
	footnote: {
		...type.caption,
		color: colors.textFaint,
	},
	section: {
		gap: space.sm,
	},
	sheetActions: {
		gap: 10,
	},
	sheetBody: {
		gap: space.xs,
		paddingBottom: space.sm,
	},
	sheetTitle: {
		...type.sectionTitle,
		color: colors.text,
	},
	sheetText: {
		...type.bodySmall,
		color: colors.textSecondary,
	},
	errorText: {
		...type.bodySmall,
		color: colors.danger,
		marginTop: space.sm,
	},
});

export default MyLeaveView;
